import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

/**
 * ResourceCard component - Displays a summary of a resource
 * Updated to handle direct Supabase data format with fallbacks for missing fields
 */
const ResourceCard = ({ resource }) => {
  // Default image if none provided
  const imageUrl = resource.featured_image_url || '/images/placeholder-resource.jpg';
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  // Truncate text with ellipsis
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden transition-shadow hover:shadow-md">
      <Link to={`/resources/${resource.slug}`}>
        <div className="relative pb-48 overflow-hidden">
          <img
            className="absolute inset-0 h-full w-full object-cover transform hover:scale-105 transition-transform duration-200"
            src={imageUrl}
            alt={resource.title}
            onError={(e) => {
              e.target.src = '/images/placeholder-resource.jpg';
            }}
          />
          {resource.has_audio && (
            <div className="absolute top-2 right-2 bg-primary-600 text-white p-1 rounded">
              <svg 
                className="w-4 h-4" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" 
                  clipRule="evenodd" 
                />
              </svg>
            </div>
          )}
        </div>
      </Link>
      
      <div className="p-4">
        <div className="flex items-center text-sm text-gray-500 mb-1">
          <span>{formatDate(resource.published_at || resource.created_at)}</span>
          {resource.category_name && (
            <>
              <span className="mx-2">•</span>
              <span>{resource.category_name}</span>
            </>
          )}
        </div>
        
        <Link to={`/resources/${resource.slug}`}>
          <h3 className="text-lg font-bold text-gray-900 mb-2 hover:text-primary-600">
            {resource.title}
          </h3>
        </Link>
        
        <p className="text-gray-600 text-sm mb-4">
          {truncateText(resource.summary, 120)}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center">
              <svg 
                className="w-4 h-4 mr-1" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" 
                  clipRule="evenodd" 
                />
              </svg>
              <span>{resource.like_count || resource.likes_count || 0}</span>
            </div>
            
            <div className="flex items-center">
              <svg 
                className="w-4 h-4 mr-1" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" 
                  clipRule="evenodd" 
                />
              </svg>
              <span>{resource.comment_count || resource.comments_count || 0}</span>
            </div>
            
            <div className="flex items-center">
              <svg 
                className="w-4 h-4 mr-1" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M10 12a2 2 0 100-4 2 2 0 000 4z" 
                  clipRule="evenodd" 
                />
                <path 
                  fillRule="evenodd" 
                  d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" 
                  clipRule="evenodd" 
                />
              </svg>
              <span>{resource.view_count || 0}</span>
            </div>
          </div>
          
          <Link 
            to={`/resources/${resource.slug}`} 
            className="text-primary-600 hover:text-primary-800 font-medium text-sm"
          >
            Read More →
          </Link>
        </div>
      </div>
    </div>
  );
};

ResourceCard.propTypes = {
  resource: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    summary: PropTypes.string,
    featured_image_url: PropTypes.string,
    category_name: PropTypes.string,
    created_at: PropTypes.string,
    published_at: PropTypes.string,
    has_audio: PropTypes.bool,
    like_count: PropTypes.number,
    comment_count: PropTypes.number,
    view_count: PropTypes.number,
    // For backward compatibility
    likes_count: PropTypes.number,
    comments_count: PropTypes.number
  }).isRequired
};

export default ResourceCard; 